import React from 'react';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { sendBrazeEvent } from 'apps/auth/analytics/braze';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import ResidenceAddressUpdatePopup from 'apps/auth/components/common/popups/ResidenceAddressUpdatePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import { getRecipientTypeParam, providerCategories } from 'apps/auth/components/utils/AmplitudeUtils';
import SupportPopup from 'apps/auth/components/utils/popup/SupportPopup';
import { NOTIFY_RECIPIENT_CLICKED, REPEAT_TRANSFER_BUTTON_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { NETWORK, PAYMENT_METHOD_AMPLITUDE, PAYOUT_COUNTRY, PAYOUT_METHOD, RECIPIENT_TYPE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { BR_TRANSFER_REPEAT_START } from 'apps/auth/constants/braze/brazeEvents';
import {
    CATEGORY_TRANSFER_TO_CASH,
    CATEGORY_TRANSFER_TO_CASH_UNITELLER,
    CATEGORY_TRANSFER_TO_HOME_DELIVERY,
    CATEGORY_TRANSFER_TO_INTERAC,
    CATEGORY_TRANSFER_TO_LIBRE,
    CATEGORY_TRANSFER_TO_PEOPLE_TRUST_INTERAC,
    CATEGORY_TRANSFER_TO_WECHAT,
    PS_BONUS,
    STATUS_OK,
} from 'apps/auth/constants/Constants';
import { AUTH_CARD_CONFIRM, AUTH_GLOBAL_REPEAT, AUTH_SEND_REPEAT } from 'apps/auth/constants/UrlConstants';
import { showActionButtons, showErrorType } from 'apps/auth/helpers/historyUtils';
import { capitalizeFirstLetters } from 'apps/auth/helpers/utils';
import { useCheck, useCheckMeta, useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import CashPayoutSecretCodeModal from '../modals/CashPayoutSecretCodeModal';
import InteracSecretCodeModal from '../modals/InteracSecretCodeModal';
import LibreHistoryModal from '../modals/LibreHistoryModal';
import WeixinInfoModal from '../modals/WeixinInfoModal';

const ActionButton = (props) => {
    const { chequeVerifyButton, repeatButton, interacNotifyButton, menuSupport, remindRecipientButton } = usePageMessages();
    const cheque = useCheck();
    const { canCancelTransaction, unconfirmedLinkId } = useCheckMeta();
    const { residenceAddressMustBeUpdated } = useExtra();

    const sendingMethod = providerCategories[cheque.providerFromCategory];
    const deliveryMethod = providerCategories[cheque.providerToCategory];

    const isBonusPaymentMethod = cheque.fromBalanceHolder === PS_BONUS;
    const button = cheque.waitingVerification ? chequeVerifyButton : repeatButton;
    const icon = cheque.waitingVerification ? 'card-checked' : 'retry';
    let href = `${cheque.globalProvider ? AUTH_GLOBAL_REPEAT : AUTH_SEND_REPEAT}?repeatId=${cheque.paymentId}`;
    if (cheque.recipientType) {
        href += `&type=${cheque.recipientType}`;
    }
    if (cheque.waitingVerification) {
        href = `${AUTH_CARD_CONFIRM}/${unconfirmedLinkId}/preview`;
    }

    const repeatClickHandler = (e) => {
        if (residenceAddressMustBeUpdated) {
            e?.preventDefault();
            props.popupRef.current?.open(<ResidenceAddressUpdatePopup popupRef={props.popupRef} />);
            return;
        }
        amplitude.getInstance().logEvent(REPEAT_TRANSFER_BUTTON_CLICKED, {
            [PAYOUT_COUNTRY]: cheque?.toCountry?.nameEn,
            [PAYMENT_METHOD_AMPLITUDE]: sendingMethod,
            [PAYOUT_METHOD]: deliveryMethod,
            [RECIPIENT_TYPE]: getRecipientTypeParam(cheque?.recipientType),
        });
        sendBrazeEvent(BR_TRANSFER_REPEAT_START);
        props.gtRestrictionService.checkChequeRestriction(e);
    };

    const sendUnitellerCashPickupAmplitudeEvent = (eventName) => {
        if (cheque?.toBankName) {
            const formattedNetworkName = capitalizeFirstLetters(cheque?.toBankName);
            amplitude.getInstance().logEvent(eventName, {
                [PAYOUT_COUNTRY]: cheque?.toCountry?.nameEn,
                [PAYOUT_METHOD]: deliveryMethod,
                [NETWORK]: formattedNetworkName,
            });
        }
    };

    if (
        cheque.secretCode &&
        (cheque.providerToCategory === CATEGORY_TRANSFER_TO_INTERAC || cheque.providerToCategory === CATEGORY_TRANSFER_TO_PEOPLE_TRUST_INTERAC) &&
        cheque.status > 10 &&
        cheque.status < STATUS_OK
    ) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="button"
                    isHoverable
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.modalRef.current.open(<InteracSecretCodeModal cheque={cheque} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="alert" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{interacNotifyButton}</Text>
                </Panel>
            </>
        );
    } else if (
        cheque.secretCode !== null &&
        (cheque.providerToCategory === CATEGORY_TRANSFER_TO_CASH || cheque.providerToCategory === CATEGORY_TRANSFER_TO_CASH_UNITELLER) &&
        cheque.status > 10 &&
        cheque.status < STATUS_OK
    ) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="button"
                    isHoverable
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        amplitude.getInstance().logEvent(NOTIFY_RECIPIENT_CLICKED);
                        props.modalRef.current.open(<CashPayoutSecretCodeModal cheque={cheque} sendEvent={sendUnitellerCashPickupAmplitudeEvent} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="alert" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{interacNotifyButton}</Text>
                </Panel>
            </>
        );
    } else if (cheque.providerToCategory === CATEGORY_TRANSFER_TO_WECHAT && cheque.status > 10 && cheque.status < STATUS_OK) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="button"
                    isHoverable
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.modalRef.current.open(<WeixinInfoModal cheque={cheque} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="alert" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{interacNotifyButton}</Text>
                </Panel>
            </>
        );
    } else if (showActionButtons(cheque) && !canCancelTransaction && showErrorType(props.serverError) === 'SANCTION') {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel type="button" isHoverable negativeMargin onClick={() => props.popupRef.current?.open(<SupportPopup popupRef={props.popupRef} />)}>
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="lifebuoy" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{menuSupport}</Text>
                </Panel>
            </>
        );
    } else if (cheque.providerToCategory === CATEGORY_TRANSFER_TO_HOME_DELIVERY && cheque.status > 10 && cheque.status < STATUS_OK) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="button"
                    isHoverable
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.modalRef.current.open(<CashPayoutSecretCodeModal cheque={cheque} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="alert" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{interacNotifyButton}</Text>
                </Panel>
            </>
        );
    } else if (cheque.providerToCategory === CATEGORY_TRANSFER_TO_LIBRE && cheque.status > 10 && cheque.status < STATUS_OK && cheque.toBankAlias) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="button"
                    isHoverable
                    negativeMargin
                    onClick={(e) => {
                        e.preventDefault();
                        props.modalRef.current.open(<LibreHistoryModal cheque={cheque} />);
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName="alert" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{remindRecipientButton}</Text>
                </Panel>
            </>
        );
    } else if (!isBonusPaymentMethod) {
        return (
            <>
                <Separator marginTop="0" marginBottom="16px" />
                <Panel
                    type="link"
                    isHoverable
                    negativeMargin
                    id={`pay_repeat_link_${cheque.paymentId}`}
                    href={href}
                    onClick={(e) => {
                        if (!cheque.waitingVerification) {
                            repeatClickHandler(e);
                        }
                    }}
                >
                    <DiskBadgeWrapper>
                        <DiskBadge variant="primary" iconName={icon} />
                    </DiskBadgeWrapper>
                    <Text color="primary">{button}</Text>
                </Panel>
            </>
        );
    }
};

ActionButton.propTypes = {
    gtRestrictionService: PropTypes.object.isRequired,
    toggleChat: PropTypes.func.isRequired,
    serverError: PropTypes.number.isRequired,
    popupRef: PropTypes.object.isRequired,
    modalRef: PropTypes.object.isRequired,
};
export default ActionButton;
